/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

import { parseAppConfig } from "@helpers/app-config";
import { buildType } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { retrieveKey } from "@memorang/helpers/src/storage";
/* -----------------Helpers & Hooks--------------- */
import { useNavigationContainerRef } from "expo-router";
import { supabase } from "libs/supabase";

/* -----------------Child components--------------- */
import * as Font from "expo-font";
import * as SplashScreen from "expo-splash-screen";

import { reactNavigationIntegration } from "@helpers/lib/sentry";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import TrackPlayer, { Capability } from "react-native-track-player";
import { customFonts } from "../../assets";
/* -----------------Others--------------- */
import { OneSignal } from "../libs/onesignal";
import { useAppStore } from "./useAppStore";

export default function useCachedResources() {
	const [isLoadingComplete, setLoadingComplete] = useState(false);
	const [isDarkMode, setIsDarkMode] = useState(false);

	const oneSignalAppId = useAppStore((store) => store.app.keys.oneSignalAppId);
	const setAppConfig = useAppStore((store) => store.setAppConfig);

	const ref = useNavigationContainerRef();

	useEffect(() => {
		async function setupPlayer() {
			await TrackPlayer.setupPlayer();
			await TrackPlayer.updateOptions({
				capabilities: [
					Capability.Play,
					Capability.Pause,
					Capability.SkipToNext,
					Capability.SkipToPrevious,
				],
			});
		}
		setupPlayer();
	}, []);

	useEffect(() => {
		if (ref && !isWeb) {
			reactNavigationIntegration.registerNavigationContainer(ref);
		}
	}, [ref]);

	useEffect(() => {
		async function loadResourcesAndDataAsync() {
			try {
				SplashScreen.preventAutoHideAsync();
				// Load fonts
				await Font.loadAsync(customFonts);
				const darkMode = retrieveKey("isDarkMode");
				setIsDarkMode(darkMode as boolean);
				if (oneSignalAppId && OneSignal) {
					OneSignal.initialize(oneSignalAppId);
					OneSignal.Notifications.requestPermission(true);
				}
			} catch (e) {
				console.warn(e);
			} finally {
				setLoadingComplete(true);
				SplashScreen.hideAsync();
			}
		}

		loadResourcesAndDataAsync();
	}, [oneSignalAppId]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await supabase
					.from("app_config")
					.select("*")
					.eq("id", buildType)
					.single();

				const parsedData = parseAppConfig(data);

				if (!parsedData) {
					throw new Error("No data found");
				}
				setAppConfig(parsedData);
			} catch (error) {
				console.error("❌ Error fetching custom app config:", error);
				const errorObj = new Error("Failed to fetch app config");
				captureErrorToSentry(errorObj, {
					error: JSON.stringify(error),
				});
			}
		})();
	}, [setAppConfig]);

	return {
		isLoadingComplete,
		isDarkMode,
	};
}
